/**
 * Utility functions for working with TipTap JSON content
 */

/**
 * Recursively extract all text from a TipTap JSON node and its children
 * This handles cases where text is split across multiple nodes (e.g., special characters)
 * For annotations, extracts only the label text (text attribute), not the elaborated text
 */
function extractTextFromNode(node: any): string {
    if (!node) return '';
    
    // If this is a text node, return its text
    if (node.type === 'text' && node.text) {
        // Check if this text has an annotation mark
        if (node.marks && Array.isArray(node.marks)) {
            const annotationMark = node.marks.find((mark: any) => mark.type === 'annotation');
            if (annotationMark && annotationMark.attrs && annotationMark.attrs.text) {
                // Return the annotation label text, not the elaborated text
                return annotationMark.attrs.text;
            }
        }
        return node.text;
    }
    
    // If this node has content, recursively extract text from all children
    if (node.content && Array.isArray(node.content)) {
        return node.content.map((child: any) => extractTextFromNode(child)).join('');
    }
    
    return '';
}

/**
 * Extract plain text from TipTap JSON content while preserving structure
 * Preserves paragraph breaks and heading structure for better diff comparison
 */
export function extractPlainTextFromTipTap(json: Record<string, any> | null | undefined): string {
    if (!json || !json.content || !Array.isArray(json.content)) {
        return '';
    }
    
    const lines: string[] = [];
    
    const traverse = (nodes: any[]) => {
        nodes.forEach((node: any) => {
            if (node.type === 'heading') {
                // Extract heading text and add as a line
                const headingText = extractTextFromNode(node);
                if (headingText.trim()) {
                    lines.push(headingText.trim());
                }
            } else if (node.type === 'paragraph') {
                // Extract paragraph text
                const paragraphText = extractTextFromNode(node);
                if (paragraphText.trim()) {
                    lines.push(paragraphText.trim());
                } else {
                    // Empty paragraph - preserve as blank line
                    lines.push('');
                }
            } else if (node.type === 'bulletList' || node.type === 'orderedList') {
                // Handle lists - extract each list item
                if (node.content && Array.isArray(node.content)) {
                    node.content.forEach((listItem: any) => {
                        if (listItem.type === 'listItem' && listItem.content) {
                            listItem.content.forEach((itemContent: any) => {
                                if (itemContent.type === 'paragraph') {
                                    const itemText = extractTextFromNode(itemContent);
                                    if (itemText.trim()) {
                                        lines.push(`• ${itemText.trim()}`);
                                    }
                                }
                            });
                        }
                    });
                }
            } else if (node.type === 'blockquote') {
                // Extract blockquote text
                const quoteText = extractTextFromNode(node);
                if (quoteText.trim()) {
                    lines.push(`> ${quoteText.trim()}`);
                }
            } else if (node.content && Array.isArray(node.content)) {
                // Recursively traverse other node types
                traverse(node.content);
            }
        });
    };
    
    traverse(json.content);
    
    return lines.join('\n');
}

/**
 * Check if a section has review changes
 * Simply checks if content_reviewed_json exists - if it does, there are review changes
 * 
 * IMPORTANT: If content_reviewed_json is null/undefined, there are no review changes (returns false)
 */
export function areTipTapContentsDifferent(
    original: Record<string, any> | null | undefined,
    reviewed: Record<string, any> | null | undefined
): boolean {
    // If content_reviewed_json exists, there are review changes
    return reviewed !== null && reviewed !== undefined;
}

